import React, { createContext, useReducer, ReactNode, useEffect, useContext } from "react";
import { appStateReducer } from "./AppReducer";
import {
    Conversation,
    ChatHistoryLoadingState,
    ChatHistoryLoadingState as ScenarioLoadingState,
    CosmosDBHealth,
    fetchConversationInNoFolder,
    historyEnsure,
    CosmosDBStatus,
    GptModelSettings,
    LanguageModelSettings,
    GlobalSettings,
    fetchGlobalSettings,
    fetchAcceptanceStatus,
    ErrorMessage,
    Scenario,
    fetchConversationFolders,
    fetchConversationInFolder,
    ConversationFolder,
    ScenarioFolder,
    fetchScenarioFolders,
    fetchScenariosInFolder,
    fetchScenariosInNoFolder,
} from "../api";

export interface AppState {
    isLeftSideOpen: boolean;
    isModelSettingsOpen: boolean;
    isGlobalSettingsOpen: boolean;
    chatHistoryLoadingState: ChatHistoryLoadingState;
    scenarioLoadingState: ScenarioLoadingState;
    isCosmosDBAvailable: CosmosDBHealth;
    chatHistory: Conversation[] | null;
    currentChat: Conversation | null;
    gptModelSettings: GptModelSettings;
    globalSettings: GlobalSettings;
    selectedScenario: Scenario | null;
    conversationFolders: Map<string, ConversationFolder>;
    scenarioFolders: Map<string, ScenarioFolder>;
    numHistoricMessages: number;
    acceptedTermsOfUse: boolean | null;
    errorMessage: ErrorMessage | null;
    uploadedFile: boolean;
    chatHistoryVersion: number;
}

export type Action =
    | { type: "TOGGLE_LEFT_SIDE" }
    | { type: "TOGGLE_MODEL_SETTINGS" }
    | { type: "TOGGLE_GLOBAL_SETTINGS" }
    | { type: "SET_COSMOSDB_STATUS"; payload: CosmosDBHealth }
    | { type: "UPDATE_CHAT_HISTORY_LOADING_STATE"; payload: ChatHistoryLoadingState }
    | { type: "UPDATE_SCENARIO_LOADING_STATE"; payload: ScenarioLoadingState }
    | { type: "UPDATE_CURRENT_CHAT"; payload: Conversation | null }
    | { type: "UPDATE_CHAT_HISTORY"; payload: Conversation } // API Call
    | { type: "UPDATE_CHAT_TITLE"; payload: Conversation } // API Call
    | { type: "DELETE_CHAT_ENTRY"; payload: Conversation } // API Call
    | { type: "DELETE_CHAT_HISTORY" } // API Call
    | { type: "DELETE_CURRENT_CHAT_MESSAGES"; payload: string } // API Call
    | { type: "FETCH_CHAT_HISTORY_NO_FOLDER"; payload: Conversation[] | null } // API Call
    | { type: "UPDATE_GPT_MODEL_NAME"; payload: string }
    | { type: "UPDATE_GPT_MODEL_SETTINGS"; payload: LanguageModelSettings }
    | { type: "UPDATE_SELECTED_SCENARIO"; payload: Scenario | null }
    | { type: "UPDATE_GLOBAL_SETTINGS"; payload: GlobalSettings }
    | { type: "FETCH_CONVERSATION_FOLDERS"; payload: ConversationFolder[] }
    | { type: "ADD_CONVERSATION_FOLDER"; payload: ConversationFolder }
    | { type: "DELETE_CONVERSATION_FOLDER"; payload: ConversationFolder }
    | { type: "UPDATE_CONVERSATION_FOLDER"; payload: ConversationFolder }
    | {
          type: "MOVE_CONVERSATION";
          payload: {
              conversationId: string;
              oldFolderId: string;
              newFolderId: string;
          };
      }
    | { type: "ADD_LOADED_MESSAGES"; payload: Conversation }
    | { type: "FETCH_SCENARIO_FOLDERS"; payload: ScenarioFolder[] }
    | { type: "ADD_SCENARIO_FOLDER"; payload: ScenarioFolder }
    | { type: "DELETE_SCENARIO_FOLDER"; payload: ScenarioFolder }
    | { type: "UPDATE_SCENARIO_FOLDER"; payload: ScenarioFolder }
    | {
          type: "MOVE_SCENARIO";
          payload: {
              scenarioId: string;
              oldFolderScenarioId: string;
              newFolderScenarioId: string;
          };
      }
    | { type: "UPDATE_SCENARIO"; payload: Scenario }
    | { type: "ADD_SCENARIO"; payload: Scenario }
    | { type: "DELETE_SCENARIO"; payload: Scenario } // API Call;
    | { type: "UPDATE_NUM_HISTORIC_MESSAGES"; payload: number }
    | { type: "SET_ERROR"; payload: ErrorMessage | null }
    | { type: "UPLOADED_FILE"; payload: boolean }
    | { type: "UPDATE_ACCEPTED_TERMS_OF_USE"; payload: boolean };

const initialState: AppState = {
    isLeftSideOpen: true,
    isModelSettingsOpen: false,
    isGlobalSettingsOpen: false,
    chatHistoryLoadingState: ChatHistoryLoadingState.Loading,
    scenarioLoadingState: ScenarioLoadingState.Loading,
    chatHistory: null,
    currentChat: null,
    isCosmosDBAvailable: {
        cosmosDB: false,
        status: CosmosDBStatus.NotConfigured,
    },
    gptModelSettings: {
        name: "gpt-4.1",
        settings: {
            top_p: 1,
            temperature: 1,
            presence_penalty: 0,
            frequency_penalty: 0,
            max_tokens: 4096,
        } as LanguageModelSettings,
    },
    globalSettings: {
        instruction: "",
        showNumHistoricMessages: false,
    },
    selectedScenario: null,
    conversationFolders: new Map(),
    scenarioFolders: new Map(),
    numHistoricMessages: 20,
    acceptedTermsOfUse: true,
    errorMessage: null,
    uploadedFile: false,
    chatHistoryVersion: 0,
};

export const AppStateContext = createContext<
    | {
          state: AppState;
          dispatch: React.Dispatch<Action>;
      }
    | undefined
>(undefined);

type AppStateProviderProps = {
    children: ReactNode;
};

export const AppStateProvider: React.FC<AppStateProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(appStateReducer, initialState);

    // Check for cosmosdb config and fetch initial data here
    const fetchChatHistory = async (offset = 0): Promise<Conversation[] | null> => {
        return await fetchConversationInNoFolder(-1, offset)
            .then((response) => {
                if (response) {
                    dispatch({ type: "FETCH_CHAT_HISTORY_NO_FOLDER", payload: response });
                } else {
                    dispatch({ type: "FETCH_CHAT_HISTORY_NO_FOLDER", payload: null });
                }
                return response;
            })
            .catch((err) => {
                dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Fail });
                dispatch({ type: "FETCH_CHAT_HISTORY_NO_FOLDER", payload: null });
                console.error("There was an issue fetching your data.");
                return null;
            });
    };

    const fetchFolderConversations = async () => {
        // Fetch folders
        const folders = await fetchConversationFolders();
        if (!folders) {
            return;
        }
        dispatch({ type: "FETCH_CONVERSATION_FOLDERS", payload: folders });

        // Fetch conversations in each folder
        if (!folders) {
            return;
        }
        for (const [_, folder] of folders.entries()) {
            const folderConversations = await fetchConversationInFolder(folder.id);
            if (folderConversations) {
                folder.conversations = folderConversations;
                dispatch({ type: "UPDATE_CONVERSATION_FOLDER", payload: folder });
            }
        }
    };

    const getHistoryEnsure = async () => {
        dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Loading });
        historyEnsure()
            .then(async (response) => {
                if (response?.cosmosDB) {
                    const chatHistory = await fetchChatHistory();
                    if (chatHistory) {
                        dispatch({
                            type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
                            payload: ChatHistoryLoadingState.Success,
                        });
                        dispatch({
                            type: "SET_COSMOSDB_STATUS",
                            payload: { cosmosDB: true, status: CosmosDBStatus.Working },
                        });
                    } else {
                        dispatch({
                            type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
                            payload: ChatHistoryLoadingState.Fail,
                        });
                        dispatch({
                            type: "SET_COSMOSDB_STATUS",
                            payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking },
                        });
                    }
                } else {
                    dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Fail });
                    dispatch({
                        type: "SET_COSMOSDB_STATUS",
                        payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking },
                    });
                }
            })
            .catch((err) => {
                dispatch({ type: "UPDATE_CHAT_HISTORY_LOADING_STATE", payload: ChatHistoryLoadingState.Fail });
                dispatch({
                    type: "SET_COSMOSDB_STATUS",
                    payload: { cosmosDB: false, status: CosmosDBStatus.NotWorking },
                });
            });
    };
    const getGlobalSettings = async () => {
        fetchGlobalSettings()
            .then((response) => {
                dispatch({ type: "UPDATE_GLOBAL_SETTINGS", payload: response as GlobalSettings });
            })
            .catch((_err: any) => {
                console.error("There was an issue fetching your data.");
            });
    };

    const getAcceptanceStatus = async () => {
        fetchAcceptanceStatus()
            .then((response) => {
                dispatch({ type: "UPDATE_ACCEPTED_TERMS_OF_USE", payload: response as boolean });
            })
            .catch((_err: any) => {
                console.error("There was an issue fetching your data.");
            });
    };

    const fetchScenarios = async () => {
        try {
            // Fetch folders
            const folders = await fetchScenarioFolders();
            if (!folders) {
                return;
            }
            dispatch({ type: "FETCH_SCENARIO_FOLDERS", payload: folders });

            // fetch scenarios in no folder (default)
            const noFolderScenarios = await fetchScenariosInNoFolder();
            const noneFolder = folders.find((folder) => folder.id === "-100");
            if (!noneFolder) {
                const constructNoneFolder = {
                    id: "-100",
                    name: "Neue Szenarien",
                    scenarios: noFolderScenarios,
                };
                dispatch({ type: "ADD_SCENARIO_FOLDER", payload: constructNoneFolder });
            } else {
                noneFolder.scenarios = noFolderScenarios;
                dispatch({ type: "UPDATE_SCENARIO_FOLDER", payload: noneFolder });
            }

            // Fetch conversations in each folder
            if (!folders) {
                return;
            }
            for (const [_, folder] of folders.entries()) {
                const folderScenarios = await fetchScenariosInFolder(folder.id);
                if (folderScenarios) {
                    folder.scenarios = folderScenarios;
                    dispatch({ type: "UPDATE_SCENARIO_FOLDER", payload: folder });
                }
            }
            dispatch({
                type: "UPDATE_SCENARIO_LOADING_STATE",
                payload: ScenarioLoadingState.Success,
            });
        } catch (err) {
            console.error("There was an issue fetching scenarios");
            dispatch({ type: "UPDATE_SCENARIO_LOADING_STATE", payload: ScenarioLoadingState.Fail });
        }
    };

    useEffect(() => {
        getHistoryEnsure();
        fetchFolderConversations();
        getGlobalSettings();
        fetchScenarios();
        getAcceptanceStatus();
    }, []);

    return <AppStateContext.Provider value={{ state, dispatch }}>{children}</AppStateContext.Provider>;
};
